import styled from "styled-components";

import * as css from "styles/CssVariables.js";
import {
  dateFormatted,
  getTimeAMPM,
  numberWithCommas,
  monthFromMonthCode,
} from "modules/functions";

import ButtonRoundIcon from "components/buttons/ButtonRoundIcon";

import blockIcon from "assets/img/icons/icons8-unavailable.svg";
import bankIcon from "assets/img/icons/icons8-bank.png";
import bitcoinIcon from "assets/img/icons/icons8-bitcoin.svg";
import skrillIcon from "assets/img/icons/icons8-skrill.svg";
import leftIcon from "assets/img/icons/left-arrow.svg";
import rightIcon from "assets/img/icons/right-arrow.svg";
import leftWhiteIcon from "assets/img/icons/left-arrow-white.svg";
import rightWhiteIcon from "assets/img/icons/right-arrow-white.svg";
import tetherIcon from "assets/img/icons/icons8-tether.svg";
import creditcardIcon from "assets/img/icons/icons8-magnetic-card.svg";
import stripeIcon from "assets/img/icons/icons8-stripe.svg";
import confirmoIcon from "assets/img/icons/confirmo-icon.svg";
import fileIcon from "assets/img/icons/file-icon.svg";
import paypalIcon from "assets/img/icons/icons8-paypal.svg";

const BillingHistoryBox = styled.div`
  width: 100%;
  min-height: 280px;
  padding: ${css.BOX_PADDING_PRIMARY};
  box-sizing: border-box;

  background: ${css.BLUE_BOX_GRADIENT};
  border-radius: ${css.BOX_RADIUS_PRIMARY};

  display: flex;
  justify-content: space-between;
  flex-direction: column;
  gap: 10px;
`;

const TableWrap = styled.div`
  width: 100%;
  padding-bottom: 5px;
  margin-bottom: -5px;

  overflow-x: auto;
`;

const ListTable = styled.table`
  width: 100%;
  min-width: 560px;
  max-width: 700px;
`;

const TableHead = styled.thead``;

const Label = styled.th`
  max-width: ${(props) => (props.width ? props.width : "auto")};
  padding: 0 5px 12px 5px;

  font-size: 18px;
  font-weight: 400;
  text-align: left;
`;

const TableBody = styled.tbody``;

const Cell = styled.td`
  padding: 0 5px;
  white-space: nowrap;
`;

const AmountBox = styled.div`
  min-width: 130px;

  display: flex;
  align-items: center;
  gap: 5px;
`;

const StripeMethodIcon = styled.div`
  height: 28px;

  transform: translateY(1px);
`;

const ConfirmoMethodIcon = styled.div`
  height: 20px;
  padding: 4px 0 4px 1px;

  transform: translateY(1px);
`;

const PaypalMethodIcon = styled.div`
  height: 22px;
  padding: 4px 0 4px 1px;

  transform: translateY(1px);
`;

const InvoiceWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;

  &:hover {
    cursor: pointer;
  }
`;

const InvoiceIcon = styled.div`
  width: 14px;
  min-height: 14px;

  transform: translateY(1px);
`;

const InvoiceNumber = styled.div`
  color: ${css.LIGHT_BLUE};
`;

const Pagination = styled.div`
  width: 100%;
  margin-top: ${css.VERTICAL_BOX_MARGIN_MEDIUM};

  display: flex;
  justify-content: center;
  gap: ${css.BUTTONS_GAP_SMALL};
`;

const NoResults = styled.div`
  width: 100%;
  margin-top: 75px;

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${css.VERTICAL_BOX_MARGIN};
`;

const NoResultsIcon = styled.div`
  width: 32px;
  min-width: 32px;
  height: 32px;
`;

const NoResultsText = styled.div``;

export default ({ data, page, limit, paginate, onDownloadClick }) => {
  const { payments, numOfResults } = data.billingHistory;

  const methodOptions = {
    bank: {
      name: "Bank Transfer",
      icon: bankIcon,
    },
    skrill: {
      name: "Skrill",
      icon: skrillIcon,
    },
    bitcoin: {
      name: "Bitcoin",
      icon: bitcoinIcon,
    },
    usdt: {
      name: "USDT",
      icon: tetherIcon,
    },
  };

  const list = payments.map((payment) => {
    const {
      id,
      created_at,
      provider,
      item,
      amount,
      tax,
      invoice_number,
      invoice_filename,
    } = payment;

    let itemText;

    switch (item) {
      case "10000_coins":
        itemText = "10,000 Coins";
        break;
      case "40000_coins":
        itemText = "40,000 Coins";
        break;
      case "200000_coins":
        itemText = "200,000 Coins";
        break;
      case "1000000_coins":
        itemText = "1,000,000 Coins";
        break;
      case "5000000_coins":
        itemText = "5,000,000 Coins";
        break;
      case "60000_coins":
        itemText = "60,000 Coins";
        break;
      case "300000_coins":
        itemText = "300,000 Coins";
        break;
      case "2000000_coins":
        itemText = "2,000,000 Coins";
        break;
      case "10000000_coins":
        itemText = "10,000,000 Coins";
        break;
      case "bronze_ad_plan":
        itemText = "Bronze Ad Plan";
        break;
      case "silver_ad_plan":
        itemText = "Silver Ad Plan";
        break;
      case "gold_20_ad_plan":
        itemText = "Gold 20 Ad Plan";
        break;
      case "gold_40_ad_plan":
        itemText = "Gold 40 Ad Plan";
        break;
      case "gold_60_ad_plan":
        itemText = "Gold 60 Ad Plan";
        break;
      case "gold_80_ad_plan":
        itemText = "Gold 80 Ad Plan";
        break;
      case "gold_100_ad_plan":
        itemText = "Gold 100 Ad Plan";
        break;
    }

    const totalAmount = (amount + tax) / 100;

    return (
      <tr key={id}>
        <Cell>
          {dateFormatted(new Date(created_at), false) +
            ", " +
            getTimeAMPM(new Date(created_at))}
        </Cell>
        <Cell>{itemText}</Cell>
        <Cell>
          <AmountBox>
            ${totalAmount} USD
            {provider === "stripe" && (
              <StripeMethodIcon>
                <img src={stripeIcon} style={{ height: "100%" }} />
              </StripeMethodIcon>
            )}
            {provider === "confirmo" && (
              <ConfirmoMethodIcon>
                <img src={confirmoIcon} style={{ height: "100%" }} />
              </ConfirmoMethodIcon>
            )}
            {provider === "paypal" && (
              <PaypalMethodIcon>
                <img src={paypalIcon} style={{ height: "100%" }} />
              </PaypalMethodIcon>
            )}
          </AmountBox>
        </Cell>
        <Cell>
          <InvoiceWrap onClick={() => onDownloadClick(invoice_filename)}>
            <InvoiceIcon>
              <img src={fileIcon} style={{ width: "100%" }} />
            </InvoiceIcon>
            <InvoiceNumber>{invoice_number}</InvoiceNumber>
          </InvoiceWrap>
        </Cell>
      </tr>
    );
  });

  let showPagination = false;

  if (numOfResults > limit) showPagination = true;

  const maxPage = Math.ceil(numOfResults / limit);

  return (
    <BillingHistoryBox>
      <TableWrap className="table-wrap">
        {list.length > 0 && (
          <ListTable>
            <TableHead>
              <tr>
                <Label width="150px">Date</Label>
                <Label width="180px">Item</Label>
                <Label width="90px">Amount</Label>
                <Label>Invoice</Label>
              </tr>
            </TableHead>
            <TableBody>{list}</TableBody>
          </ListTable>
        )}
        {list.length === 0 && (
          <NoResults>
            <NoResultsIcon>
              <img src={blockIcon} style={{ width: "100%" }} />
            </NoResultsIcon>
            <NoResultsText>You don't have any invoices.</NoResultsText>
          </NoResults>
        )}
      </TableWrap>
      {showPagination && (
        <Pagination>
          {page > 1 && (
            <ButtonRoundIcon
              icon={leftIcon}
              hoverIcon={leftWhiteIcon}
              background={css.BRAND_YELLOW}
              hoverBackground={css.BRAND_GREEN}
              iconSize="16px"
              buttonSize="32px"
              onClick={() => paginate("previous")}
            />
          )}
          {page < maxPage && (
            <ButtonRoundIcon
              icon={rightIcon}
              hoverIcon={rightWhiteIcon}
              background={css.BRAND_YELLOW}
              hoverBackground={css.BRAND_GREEN}
              iconSize="16px"
              buttonSize="32px"
              onClick={() => paginate("next")}
            />
          )}
        </Pagination>
      )}
    </BillingHistoryBox>
  );
};
